
import React, {
    useState,
    useEffect,
    useCallback
} from "react";

import { AdminSideMenu } from "../admin/AdminMenu";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";
import API_URL from "../../api/API";

// ============================================
// CONFIGURATION
// ============================================

const POLL_INTERVAL_MS = 2500;

const isRunning = (status) =>
    status === "pending" || status === "running";

const isFinished = (status) =>
    status === "completed" || status === "failed";

const statusStyles = {
    pending: "bg-gray-100 text-gray-700",
    running: "bg-blue-100 text-blue-700",
    passed: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
    error: "bg-red-100 text-red-700",
    needs_review: "bg-yellow-100 text-yellow-800",
    completed: "bg-green-100 text-green-700"
};

const StatusBadge = ({ status }) => (
    <span
        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
            statusStyles[status] || statusStyles.pending
        }`}
    >
        {(status || "pending").replace(/_/g, " ")}
    </span>
);

// ============================================
// ADMIN EVALUATIONS
// ============================================

const AdminEvaluations = () => {

    const { admin } = useAdminAuthContext();

    const [cases, setCases] = useState([]);
    const [runs, setRuns] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    const [selectedCategory, setSelectedCategory] = useState("");
    const [search, setSearch] = useState("");

    const [activeRun, setActiveRun] = useState(null);
    const [selectedResult, setSelectedResult] = useState(null);

    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [starting, setStarting] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // ========================================
    // API HELPER
    // ========================================

    const apiRequest = useCallback(async (url, options = {}) => {

        if (!admin?.token) {
            throw new Error("Administrator authentication required.");
        }

        const response = await fetch(url, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${admin.token}`,
                ...(options.headers || {})
            }
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(
                data.message ||
                data.error ||
                `Request failed (${response.status})`
            );
        }

        return data;

    }, [admin?.token]);

    // ========================================
    // FETCH CASES
    // ========================================

    const fetchCases = useCallback(async () => {

        const data = await apiRequest(
            API_URL.ADMIN_EVAL_CASES
        );

        setCases(data.cases || []);

        return data.cases || [];

    }, [apiRequest]);

    // ========================================
    // FETCH RUN HISTORY
    // ========================================

    const fetchRuns = useCallback(async () => {

        const data = await apiRequest(
            API_URL.ADMIN_EVAL_RUNS
        );

        const list = data.runs || [];

        setRuns(list);

        return list;

    }, [apiRequest]);

    // ========================================
    // INITIAL LOAD
    // ========================================

    useEffect(() => {

        if (!admin?.token) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {

            try {

                setLoading(true);
                setError("");

                const [casesData, runsData] = await Promise.all([
                    apiRequest(API_URL.ADMIN_EVAL_CASES),
                    apiRequest(API_URL.ADMIN_EVAL_RUNS)
                ]);

                if (cancelled) return;

                setCases(casesData.cases || []);
                setRuns(runsData.runs || []);

                // Restore any running or pending evaluation
                // after refreshing the browser.

                const runningRun = (runsData.runs || []).find(
                    item => isRunning(item.status)
                );

                if (runningRun) {

                    const data = await apiRequest(
                        API_URL.ADMIN_EVAL_RUNS + "/" + runningRun._id
                    );

                    if (!cancelled) {
                        setActiveRun(data.run);
                    }
                }

            } catch (err) {

                if (!cancelled) {
                    setError(err.message);
                }

            } finally {

                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };

    }, [admin?.token, apiRequest]);

    // ========================================
    // POLL ACTIVE EVALUATION
    // ========================================

    useEffect(() => {

        if (!activeRun?._id || !admin?.token) {
            return;
        }

        if (isFinished(activeRun.status)) {
            return;
        }

        let cancelled = false;
        let inFlight = false;

        const runId = activeRun._id;

        const poll = async () => {

            if (cancelled || inFlight) return;

            inFlight = true;

            try {

                const data = await apiRequest(
                    API_URL.ADMIN_EVAL_RUNS + "/" + runId
                );

                if (cancelled) return;

                if (!data.run) {
                    throw new Error("Evaluation run not found.");
                }

                setActiveRun(data.run);

                // Keep history updated as the run progresses.
                if (isFinished(data.run.status)) {
                    await fetchRuns();
                }

            } catch (err) {

                if (!cancelled) {
                    setError(err.message);
                }

            } finally {

                inFlight = false;
            }
        };

        poll();

        const interval = setInterval(
            poll,
            POLL_INTERVAL_MS
        );

        return () => {
            cancelled = true;
            clearInterval(interval);
        };

    }, [
        activeRun?._id,
        activeRun?.status,
        admin?.token,
        apiRequest,
        fetchRuns
    ]);

    // ========================================
    // SYNC DEFAULT CASES
    // ========================================

    const importCases = async () => {

        if (importing || starting) return;

        try {

            setImporting(true);
            setError("");
            setSuccess("");

            const data = await apiRequest(
                API_URL.ADMIN_EVAL_IMPORT,
                {
                    method: "POST"
                }
            );

            const refreshedCases = await fetchCases();

            // Remove selected IDs that no longer exist
            // or have been deactivated.

            setSelectedIds(previous =>
                previous.filter(id =>
                    refreshedCases.some(item =>
                        item._id === id && item.active
                    )
                )
            );

            const created = data.created ?? 0;
            const updated = data.updated;

            setSuccess(
                updated !== undefined
                    ? `Sync complete: ${created} created, ${updated} updated.`
                    : `Sync complete: ${created} created, ${data.skipped ?? 0} already existed.`
            );

        } catch (err) {

            setError(err.message);

        } finally {

            setImporting(false);
        }
    };

    // ========================================
    // FILTERING
    // ========================================

    const categories = [
        ...new Set(
            cases
                .map(item => item.category)
                .filter(Boolean)
        )
    ].sort();

    const filteredCases = cases.filter(item => {

        const matchesCategory =
            !selectedCategory ||
            item.category === selectedCategory;

        const term = search.trim().toLowerCase();

        const matchesSearch =
            !term ||
            (item.title || "").toLowerCase().includes(term) ||
            (item.expectedBehavior || "").toLowerCase().includes(term) ||
            (item.productKey || "").toLowerCase().includes(term);

        return matchesCategory && matchesSearch;
    });

    const selectableCases = filteredCases.filter(
        item => item.active
    );

    const allSelected =
        selectableCases.length > 0 &&
        selectableCases.every(item =>
            selectedIds.includes(item._id)
        );

    // ========================================
    // SELECTION
    // ========================================

    const toggleCase = (id) => {

        setSelectedIds(previous =>
            previous.includes(id)
                ? previous.filter(item => item !== id)
                : [...previous, id]
        );
    };

    const toggleAll = () => {

        const visibleIds = selectableCases.map(
            item => item._id
        );

        if (allSelected) {

            setSelectedIds(previous =>
                previous.filter(
                    id => !visibleIds.includes(id)
                )
            );

        } else {

            setSelectedIds(previous => [
                ...new Set([
                    ...previous,
                    ...visibleIds
                ])
            ]);
        }
    };

    // ========================================
    // RUNNING STATE
    // ========================================

    const evaluationRunning =
        isRunning(activeRun?.status) ||
        runs.some(item => isRunning(item.status));

    const activeCaseCount = cases.filter(
        item => item.active
    ).length;

    // ========================================
    // START EVALUATION
    // ========================================

    const startRun = async (runAll = false) => {

        if (starting || evaluationRunning) {
            setError(
                "An evaluation is already running. Wait for it to finish."
            );
            return;
        }

        const activeIds = new Set(
            cases
                .filter(item => item.active)
                .map(item => item._id)
        );

        const validSelectedIds = selectedIds.filter(
            id => activeIds.has(id)
        );

        if (!runAll && validSelectedIds.length === 0) {
            setError("Select at least one active evaluation case.");
            return;
        }

        if (runAll && activeCaseCount === 0) {
            setError("No active evaluation cases are available.");
            return;
        }

        const count = runAll
            ? activeCaseCount
            : validSelectedIds.length;

        const confirmed = window.confirm(
            `Run ${count} evaluation case${count === 1 ? "" : "s"}? This will use OpenAI API credits.`
        );

        if (!confirmed) return;

        try {

            setStarting(true);
            setError("");
            setSuccess("");
            setSelectedResult(null);

            const data = await apiRequest(
                API_URL.ADMIN_EVAL_RUN,
                {
                    method: "POST",
                    body: JSON.stringify({
                        caseIds: runAll ? [] : validSelectedIds
                    })
                }
            );

            if (!data.runId) {
                throw new Error(
                    "Backend did not return an evaluation run ID."
                );
            }

            const runData = await apiRequest(
                API_URL.ADMIN_EVAL_RUNS + "/" + data.runId
            );

            setActiveRun(runData.run);

            await fetchRuns();

            setSuccess("Evaluation started successfully.");

        } catch (err) {

            setError(err.message);

            // Refresh history if the request created
            // a run but fetching its details failed.
            try {
                await fetchRuns();
            } catch (_) {
                // Keep the original error.
            }

        } finally {

            setStarting(false);
        }
    };

    // ========================================
    // OPEN HISTORICAL RUN
    // ========================================

    const openRun = async (runId) => {

        try {

            setError("");
            setSelectedResult(null);

            const data = await apiRequest(
                API_URL.ADMIN_EVAL_RUNS + "/" + runId
            );

            if (!data.run) {
                throw new Error("Evaluation run not found.");
            }

            setActiveRun(data.run);

        } catch (err) {

            setError(err.message);
        }
    };

    // ========================================
    // SUMMARY
    // ========================================

    const summary = activeRun ? [
        {
            label: "Total",
            value: activeRun.totalCases ?? 0
        },
        {
            label: "Completed",
            value: activeRun.completedCases ?? 0
        },
        {
            label: "Passed",
            value: activeRun.passedCases ?? 0
        },
        {
            label: "Failed",
            value: activeRun.failedCases ?? 0
        },
        {
            label: "Review",
            value: activeRun.reviewCases ?? 0
        },
        {
            label: "Errors",
            value: activeRun.errorCases ?? 0
        }
    ] : [];

    const progress = activeRun?.totalCases
        ? Math.min(
            100,
            Math.round(
                ((activeRun.completedCases || 0) /
                    activeRun.totalCases) * 100
            )
        )
        : 0;

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="flex min-h-screen bg-black">

            {/* ADMIN SIDEBAR */}

            <div className="bg-black self-stretch flex-shrink-0">
                <AdminSideMenu />
            </div>

            {/* MAIN CONTENT */}

            <div className="flex-1 min-w-0 bg-gray-100 p-4 sm:p-6 pt-16">

                {/* HEADER */}

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">

                    <div>

                        <h1 className="text-2xl font-bold text-gray-900">
                            AI Evaluations
                        </h1>

                        <p className="text-sm text-gray-500 mt-1">
                            Test the RAZON AI Product Advisor against defined scenarios.
                        </p>

                    </div>

                    <div className="flex flex-wrap gap-2">

                        <button
                            type="button"
                            onClick={importCases}
                            disabled={importing || starting}
                            className="px-4 py-2 border border-gray-300 bg-white rounded hover:bg-gray-50 disabled:opacity-50 text-sm"
                        >
                            {importing
                                ? "Syncing..."
                                : "Sync Evaluation Cases"}
                        </button>

                        <button
                            type="button"
                            onClick={() => startRun(false)}
                            disabled={
                                loading ||
                                starting ||
                                importing ||
                                evaluationRunning ||
                                selectedIds.length === 0
                            }
                            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 text-sm"
                        >
                            {starting
                                ? "Starting..."
                                : `Run Selected (${selectedIds.length})`}
                        </button>

                        <button
                            type="button"
                            onClick={() => startRun(true)}
                            disabled={
                                loading ||
                                starting ||
                                importing ||
                                evaluationRunning ||
                                activeCaseCount === 0
                            }
                            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-700 disabled:opacity-50 text-sm"
                        >
                            Run All
                        </button>

                    </div>

                </div>

                {/* ERROR */}

                {error && (

                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                        {error}
                    </div>

                )}

                {/* SUCCESS */}

                {success && (

                    <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm">
                        {success}
                    </div>

                )}

                {/* RUNNING INDICATOR */}

                {evaluationRunning && (

                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm">
                        An evaluation is pending or running. New evaluation runs are temporarily disabled.
                    </div>

                )}

                {/* EVALUATION CASES */}

                <div className="bg-white border rounded-lg shadow-sm mb-6">

                    <div className="p-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3">

                        <div>

                            <h2 className="text-lg font-semibold">
                                Evaluation Cases
                            </h2>

                            <p className="text-xs text-gray-500 mt-1">
                                {cases.length} cases available
                                {" · "}
                                {activeCaseCount} active
                            </p>

                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">

                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search evaluation cases"
                                className="px-3 py-2 border rounded text-sm"
                            />

                            <select
                                value={selectedCategory}
                                onChange={e => setSelectedCategory(e.target.value)}
                                className="px-3 py-2 border rounded text-sm bg-white"
                            >

                                <option value="">
                                    All Categories
                                </option>

                                {categories.map(category => (

                                    <option
                                        key={category}
                                        value={category}
                                    >
                                        {category}
                                    </option>

                                ))}

                            </select>

                        </div>

                    </div>

                    <div className="overflow-x-auto">

                        <table className="w-full text-sm">

                            <thead className="bg-gray-100 text-gray-600">

                                <tr>

                                    <th className="px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            disabled={
                                                selectableCases.length === 0
                                            }
                                            onChange={toggleAll}
                                        />
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Case
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Category
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Type
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Active
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {loading ? (

                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="py-10 text-center"
                                        >
                                            Loading evaluation cases...
                                        </td>
                                    </tr>

                                ) : filteredCases.length === 0 ? (

                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="py-10 text-center text-gray-500"
                                        >
                                            No evaluation cases found.
                                        </td>
                                    </tr>

                                ) : filteredCases.map(item => (

                                    <tr
                                        key={item._id}
                                        className="border-t hover:bg-gray-50"
                                    >

                                        <td className="px-4 py-3">

                                            <input
                                                type="checkbox"
                                                checked={
                                                    selectedIds.includes(item._id)
                                                }
                                                disabled={!item.active}
                                                onChange={() =>
                                                    toggleCase(item._id)
                                                }
                                            />

                                        </td>

                                        <td className="px-4 py-3">

                                            <div className="font-medium text-gray-900">
                                                {item.title}
                                            </div>

                                            <div className="text-xs text-gray-500 mt-1 max-w-xl">
                                                {item.expectedBehavior}
                                            </div>

                                            {item.productKey && (

                                                <div className="text-xs text-gray-400 mt-1">
                                                    Product: {item.productKey}
                                                </div>

                                            )}

                                        </td>

                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {item.category}
                                        </td>

                                        <td className="px-4 py-3">
                                            {item.evaluationType}
                                        </td>

                                        <td className="px-4 py-3">
                                            {item.active ? "Yes" : "No"}
                                        </td>

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </div>

                </div>

                {/* EVALUATION RESULTS */}

                {activeRun && (

                    <div className="bg-white border rounded-lg shadow-sm mb-6 p-5">

                        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">

                            <div>

                                <h2 className="text-lg font-semibold">
                                    Evaluation Results
                                </h2>

                                <p className="text-xs text-gray-500 mt-1 break-all">
                                    Run ID: {activeRun._id}
                                </p>

                            </div>

                            <StatusBadge status={activeRun.status} />

                        </div>

                        {/* SUMMARY CARDS */}

                        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">

                            {summary.map(item => (

                                <div
                                    key={item.label}
                                    className="bg-gray-50 border rounded-lg p-4"
                                >

                                    <div className="text-2xl font-bold">
                                        {item.value}
                                    </div>

                                    <div className="text-xs text-gray-500 mt-1">
                                        {item.label}
                                    </div>

                                </div>

                            ))}

                        </div>

                        {/* PROGRESS */}

                        <div className="mb-5">

                            <div className="flex justify-between text-sm mb-2">

                                <span>
                                    Execution progress
                                </span>

                                <span>
                                    {progress}%
                                </span>

                            </div>

                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">

                                <div
                                    className="h-full bg-gray-800 transition-all duration-300"
                                    style={{
                                        width: `${progress}%`
                                    }}
                                />

                            </div>

                        </div>

                        {/* AVERAGE SCORE */}

                        {activeRun.averageScore !== null &&
                            activeRun.averageScore !== undefined && (

                            <p className="text-sm mb-5">

                                Average score:{" "}

                                <strong>
                                    {activeRun.averageScore}/100
                                </strong>

                            </p>

                        )}

                        {/* RUN ERROR */}

                        {activeRun.error && (

                            <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm whitespace-pre-wrap">
                                {activeRun.error}
                            </div>

                        )}

                        {/* RESULTS TABLE */}

                        <div className="overflow-x-auto">

                            <table className="w-full text-sm">

                                <thead className="bg-gray-100">

                                    <tr>

                                        <th className="px-4 py-3 text-left">
                                            Case
                                        </th>

                                        <th className="px-4 py-3 text-left">
                                            Status
                                        </th>

                                        <th className="px-4 py-3 text-left">
                                            Score
                                        </th>

                                        <th className="px-4 py-3 text-left">
                                            Action
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {(activeRun.results || []).map(
                                        (result, index) => (

                                        <tr
                                            key={result._id || index}
                                            className="border-t"
                                        >

                                            <td className="px-4 py-3">

                                                <div className="font-medium">
                                                    {result.title}
                                                </div>

                                                <div className="text-xs text-gray-500">
                                                    {result.category}
                                                </div>

                                            </td>

                                            <td className="px-4 py-3">
                                                <StatusBadge
                                                    status={result.status}
                                                />
                                            </td>

                                            <td className="px-4 py-3">
                                                {result.score ?? "-"}
                                            </td>

                                            <td className="px-4 py-3">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setSelectedResult(result)
                                                    }
                                                    className="text-blue-700 hover:underline"
                                                >
                                                    View Details
                                                </button>

                                            </td>

                                        </tr>

                                    ))}

                                </tbody>

                            </table>

                        </div>

                    </div>

                )}

                {/* EVALUATION HISTORY */}

                <div className="bg-white border rounded-lg shadow-sm">

                    <div className="p-4 border-b">

                        <h2 className="text-lg font-semibold">
                            Evaluation History
                        </h2>

                    </div>

                    <div className="overflow-x-auto">

                        <table className="w-full text-sm">

                            <thead className="bg-gray-100">

                                <tr>

                                    <th className="px-4 py-3 text-left">
                                        Date
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Status
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Cases
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Passed
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Score
                                    </th>

                                    <th className="px-4 py-3 text-left">
                                        Action
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {runs.length === 0 ? (

                                    <tr>

                                        <td
                                            colSpan={6}
                                            className="py-10 text-center text-gray-500"
                                        >
                                            No evaluation runs yet.
                                        </td>

                                    </tr>

                                ) : runs.map(run => (

                                    <tr
                                        key={run._id}
                                        className="border-t"
                                    >

                                        <td className="px-4 py-3 whitespace-nowrap">

                                            {run.createdAt
                                                ? new Date(
                                                    run.createdAt
                                                ).toLocaleString()
                                                : "-"}

                                        </td>

                                        <td className="px-4 py-3">
                                            <StatusBadge
                                                status={run.status}
                                            />
                                        </td>

                                        <td className="px-4 py-3">
                                            {run.totalCases ?? 0}
                                        </td>

                                        <td className="px-4 py-3">
                                            {run.passedCases ?? 0}
                                        </td>

                                        <td className="px-4 py-3">
                                            {run.averageScore ?? "-"}
                                        </td>

                                        <td className="px-4 py-3">

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openRun(run._id)
                                                }
                                                className="text-blue-700 hover:underline"
                                            >
                                                View Run
                                            </button>

                                        </td>

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </div>

                </div>

                {/* RESULT DETAILS MODAL */}

                {selectedResult && (

                    <div
                        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                        onClick={() => setSelectedResult(null)}
                    >

                        <div
                            className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
                            onClick={e => e.stopPropagation()}
                        >

                            <div className="flex justify-between items-start gap-4 mb-5">

                                <div>

                                    <h2 className="text-xl font-semibold">
                                        {selectedResult.title}
                                    </h2>

                                    <div className="mt-2">
                                        <StatusBadge
                                            status={selectedResult.status}
                                        />
                                    </div>

                                    {selectedResult.score !== null &&
                                        selectedResult.score !== undefined && (

                                        <p className="text-sm text-gray-600 mt-2">
                                            Score:{" "}
                                            <strong>
                                                {selectedResult.score}/100
                                            </strong>
                                        </p>

                                    )}

                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setSelectedResult(null)
                                    }
                                    className="text-gray-500 hover:text-black text-2xl"
                                    aria-label="Close result details"
                                >
                                    ×
                                </button>

                            </div>

                            <div className="space-y-5">

                                {/* TEST MESSAGES */}

                                <div>

                                    <h3 className="font-semibold mb-2">
                                        Test Messages
                                    </h3>

                                    {(selectedResult.messages || []).length === 0 ? (

                                        <p className="text-sm text-gray-500">
                                            No messages recorded.
                                        </p>

                                    ) : (

                                        (selectedResult.messages || []).map(
                                            (message, index) => (

                                            <div
                                                key={index}
                                                className="bg-gray-50 p-3 rounded mb-2 text-sm whitespace-pre-wrap"
                                            >
                                                <div className="text-xs font-semibold text-gray-500 mb-1">
                                                    Message {index + 1}
                                                </div>

                                                {message.content}
                                            </div>

                                        ))

                                    )}

                                </div>

                                {/* AI RESPONSES */}

                                <div>

                                    <h3 className="font-semibold mb-2">
                                        AI Responses
                                    </h3>

                                    {(selectedResult.responses || []).length === 0 ? (

                                        <p className="text-sm text-gray-500">
                                            No AI responses recorded yet.
                                        </p>

                                    ) : (

                                        (selectedResult.responses || []).map(
                                            (response, index) => (

                                            <div
                                                key={index}
                                                className="bg-blue-50 p-3 rounded mb-2 text-sm whitespace-pre-wrap"
                                            >
                                                <div className="text-xs font-semibold text-blue-700 mb-1">
                                                    Response {index + 1}
                                                </div>

                                                {response}
                                            </div>

                                        ))

                                    )}

                                </div>

                                {/* TOOLS */}

                                <div>

                                    <h3 className="font-semibold mb-2">
                                        Tools Used
                                    </h3>

                                    <p className="text-sm text-gray-600">

                                        {selectedResult.toolsUsed?.length
                                            ? selectedResult.toolsUsed.join(", ")
                                            : "None recorded"}

                                    </p>

                                </div>

                                {/* GRADER FEEDBACK */}

                                <div>

                                    <h3 className="font-semibold mb-2">
                                        Grader Feedback
                                    </h3>

                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">

                                        {selectedResult.feedback ||
                                            "No feedback yet."}

                                    </p>

                                </div>

                                {/* ERROR */}

                                {selectedResult.error && (

                                    <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 whitespace-pre-wrap">

                                        <h3 className="font-semibold mb-1">
                                            Execution Error
                                        </h3>

                                        {selectedResult.error}

                                    </div>

                                )}

                                {/* DURATION */}

                                {selectedResult.durationMs !== null &&
                                    selectedResult.durationMs !== undefined && (

                                    <div className="text-xs text-gray-500">

                                        Execution time:{" "}

                                        {(
                                            selectedResult.durationMs / 1000
                                        ).toFixed(2)} seconds

                                    </div>

                                )}

                            </div>

                        </div>

                    </div>

                )}

            </div>

        </div>
    );
};

export default AdminEvaluations;
